import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";
import { TournamentAchievements } from "@/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useMemo, useRef } from "react";
import { usePageVisibility } from "./useVisibilityPausedQuery";

/**
 * Real-time hook for the per-tournament achievement holders doc.
 *
 * Backed by a single Firestore doc at `tournamentAchievements/{tournamentId}`,
 * so this is a constant-cost listener regardless of team count. Pauses when
 * the page is hidden to save reads, mirroring `useTournamentMatches`.
 */
export function useTournamentAchievements(tournamentId: string) {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const queryKey = useMemo(
    () => ["tournament-achievements", tournamentId] as const,
    [tournamentId],
  );

  const pendingRef = useRef<{
    resolve: (data: TournamentAchievements | null) => void;
    reject: (error: Error) => void;
  } | null>(null);

  useEffect(() => {
    if (!isVisible) return;

    const ref = doc(db, "tournamentAchievements", tournamentId);
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        const data = snapshot.exists()
          ? (formatFirestoreDocument(snapshot) as TournamentAchievements)
          : null;
        if (pendingRef.current) {
          pendingRef.current.resolve(data);
          pendingRef.current = null;
        }
        queryClient.setQueryData(queryKey, data);
      },
      (error) => {
        console.error("Error fetching tournament achievements:", error);
        if (pendingRef.current) {
          pendingRef.current.reject(error as Error);
          pendingRef.current = null;
        }
      },
    );

    return () => {
      unsubscribe();
      if (pendingRef.current) {
        const cached =
          queryClient.getQueryData<TournamentAchievements | null>(queryKey);
        pendingRef.current.resolve(cached ?? null);
        pendingRef.current = null;
      }
    };
  }, [tournamentId, queryClient, queryKey, isVisible]);

  return useQuery({
    queryKey,
    queryFn: () =>
      new Promise<TournamentAchievements | null>((resolve, reject) => {
        const cached =
          queryClient.getQueryData<TournamentAchievements | null>(queryKey);
        if (cached !== undefined) {
          resolve(cached);
          return;
        }
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current.resolve(null);
            pendingRef.current = null;
          }
        }, 10000);
        pendingRef.current = {
          resolve: (data) => {
            clearTimeout(timeoutId);
            resolve(data);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            reject(error);
          },
        };
      }),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 5,
  });
}
